"use client";

import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// useDialog — drop-in replacement for window.alert/confirm/prompt that
// renders a styled modal matching the rest of the admin UI instead of
// the browser's native chrome (which leaks the hostname, ignores
// dark-mode preferences, and can't be styled).
//
// Usage:
//
//   const { notify, confirm, prompt, modal } = useDialog();
//
//   // somewhere in JSX:
//   {modal}
//
//   // somewhere in a handler:
//   await notify({
//     title: "Generate failed",
//     message: "Address already exists on this network.",
//     variant: "destructive",
//   });
//
//   const ok = await confirm({
//     title: "Revoke invite?",
//     message: "They won't be able to use the link they received.",
//     confirmLabel: "Revoke",
//     variant: "destructive",
//   });
//   if (!ok) return;
//
//   const reason = await prompt({
//     title: "Force-delete account",
//     message: "Reason (audit-logged):",
//     defaultValue: "Paystack flagged as invalid",
//   });
//   if (!reason) return;
//
// All three methods return Promises that resolve when the user dismisses
// the modal. notify → void; confirm → boolean; prompt → string|null
// (null on cancel, never empty string).

type NotifyOpts = {
  title: string;
  message?: string;
  // OK button label. Defaults to "OK".
  okLabel?: string;
  // destructive uses a red button + warning icon; default uses primary.
  variant?: "default" | "destructive";
};

type ConfirmOpts = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // destructive uses a red button + warning icon; default uses primary.
  variant?: "default" | "destructive";
};

type PromptOpts = ConfirmOpts & {
  defaultValue?: string;
  placeholder?: string;
  // If true, the input is a multiline textarea (for "reason" fields).
  multiline?: boolean;
  // Reject confirm if input is empty after trim. Default true.
  required?: boolean;
  maxLength?: number;
};

type DialogState =
  | { kind: "closed" }
  | ({ kind: "notify" } & NotifyOpts)
  | ({ kind: "confirm" } & ConfirmOpts)
  | ({ kind: "prompt"; value: string } & PromptOpts);

export function useDialog() {
  const [state, setState] = useState<DialogState>({ kind: "closed" });
  // resolverRef holds the promise resolver for the currently-open
  // dialog. Stored in a ref so re-renders don't drop it.
  const resolverRef = useRef<((value: unknown) => void) | null>(null);

  const close = useCallback((value: unknown) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setState({ kind: "closed" });
  }, []);

  const notify = useCallback((opts: NotifyOpts): Promise<void> => {
    return new Promise<void>((resolve) => {
      resolverRef.current = () => resolve();
      setState({ kind: "notify", ...opts });
    });
  }, []);

  const confirm = useCallback((opts: ConfirmOpts): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = (v) => resolve(v as boolean);
      setState({ kind: "confirm", ...opts });
    });
  }, []);

  const prompt = useCallback((opts: PromptOpts): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      resolverRef.current = (v) => resolve(v as string | null);
      setState({ kind: "prompt", value: opts.defaultValue ?? "", ...opts });
    });
  }, []);

  const modal = state.kind === "closed" ? null : (
    <DialogModal state={state} setState={setState} close={close} />
  );

  return { notify, confirm, prompt, modal };
}

// Internal modal renderer. Kept inside this file because nothing else
// renders DialogState — it's an implementation detail of the hook.
function DialogModal({
  state,
  setState,
  close,
}: {
  state: Exclude<DialogState, { kind: "closed" }>;
  setState: (s: DialogState) => void;
  close: (value: unknown) => void;
}) {
  const isPrompt = state.kind === "prompt";
  const isNotify = state.kind === "notify";
  const variant = state.variant ?? "default";
  const confirmLabel =
    state.kind === "notify"
      ? (state.okLabel ?? "OK")
      : (state.confirmLabel ?? (isPrompt ? "Save" : "Confirm"));
  const cancelLabel = !isNotify ? (state.cancelLabel ?? "Cancel") : null;

  function onConfirm() {
    if (state.kind === "prompt") {
      const trimmed = state.value.trim();
      if (state.required !== false && !trimmed) return;
      close(trimmed);
    } else if (state.kind === "notify") {
      close(undefined);
    } else {
      close(true);
    }
  }

  function onCancel() {
    if (state.kind === "prompt") close(null);
    else if (state.kind === "notify") close(undefined);
    else close(false);
  }

  // Render-prevention: ESC + backdrop click both cancel. The form
  // wrapper makes Enter submit (textarea handles its own Enter).
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); onConfirm(); }}
        className="w-full max-w-md bg-background border border-border rounded-lg shadow-lg"
      >
        <div className="flex items-start justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            {variant === "destructive" && <AlertTriangle size={16} className="text-warning-fg" />}
            <h2 className="text-sm font-semibold">{state.title}</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          {state.message && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{state.message}</p>
          )}

          {state.kind === "prompt" && (
            state.multiline ? (
              <textarea
                value={state.value}
                onChange={(e) => setState({ ...state, value: e.target.value })}
                placeholder={state.placeholder}
                maxLength={state.maxLength}
                rows={3}
                autoFocus
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background resize-none"
              />
            ) : (
              <input
                type="text"
                value={state.value}
                onChange={(e) => setState({ ...state, value: e.target.value })}
                placeholder={state.placeholder}
                maxLength={state.maxLength}
                autoFocus
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background"
              />
            )
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          {!isNotify && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-muted"
            >
              {cancelLabel}
            </button>
          )}
          <Button
            type="submit"
            variant={variant === "destructive" ? "destructive" : "default"}
            disabled={
              state.kind === "prompt" &&
              state.required !== false &&
              !state.value.trim()
            }
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Re-export styling helpers for tests / external composition (currently unused).
export const _internal = { cn, Loader2 };
